import React, { useState, useEffect, useCallback } from 'react';
import '../styles/friendsLeaderboardPage.css';
import TabSwitch from '../../../shared/components/TabSwitch';
import Preloader from '../../../shared/components/Preloader';
import InviteFriendsModal from './InviteFriendsModal';
import PartnershipTab from '../components/PartnershipTab';
import { socialAPI } from '../../../services/api';
import { useDispatch } from 'react-redux';
import { fetchWalletBalance } from '../../wallet/store/walletSlice';
import UserAvatar from '../../../shared/components/UserAvatar';
import { useTranslation } from 'react-i18next';

const TON_BY_MEDAL = { gold: 500, silver: 300, bronze: 200 };

const FriendsLeaderboardPage = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('partnership');
  const [friends, setFriends] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [me, setMe] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lbLoading, setLbLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const dispatch = useDispatch();

  const fetchFriends = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await socialAPI.getFriends();
      setFriends(data || []);
    } catch (err) {
      console.error('Failed to fetch friends:', err);
      setError(t('friends.load_failed', 'Failed to load friends list'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const fetchLeaderboard = useCallback(async () => {
    setLbLoading(true);
    try {
      const data = await socialAPI.getLeaderboard(50);
      setLeaderboard(data?.top || []);
      setMe(data?.me || null);
    } catch (err) {
      console.error('Leaderboard load error:', err);
    } finally {
      setLbLoading(false);
    }
  }, []);

  useEffect(() => { fetchFriends(); }, [fetchFriends]);
  useEffect(() => {
    if (tab === 'leaderboard' && leaderboard.length === 0) fetchLeaderboard();
  }, [tab, fetchLeaderboard, leaderboard.length]);

  const handleClaimTicket = async (friend) => {
    if (claimingId) return;
    setClaimingId(friend.id);
    try {
      await socialAPI.claimReferralTicket(friend.id);
      dispatch(fetchWalletBalance());
      setFriends(prev => prev.map(f =>
        f.id === friend.id ? { ...f, ticketClaimed: true } : f
      ));
    } catch (err) {
      console.error('Claim ticket failed:', err);
    } finally {
      setClaimingId(null);
    }
  };

  // Podium display order: silver (left) | gold (center) | bronze (right)
  const top3 = leaderboard.slice(0, 3); // sorted rank 1, 2, 3
  const podium = top3.length >= 2
    ? [
        top3[1] ? { ...top3[1], medal: 'silver' } : null,
        top3[0] ? { ...top3[0], medal: 'gold' }   : null,
        top3[2] ? { ...top3[2], medal: 'bronze' } : null,
      ].filter(Boolean)
    : top3.map((e, i) => ({ ...e, medal: ['gold', 'silver', 'bronze'][i] }));

  // Show me as a pinned sticky row at the bottom when I'm outside the podium —
  // exclude my inline row from the list to avoid duplication.
  const showMePinned = !!me && me.rank > 3;
  const listEntries = leaderboard
    .slice(3)
    .filter(e => !(showMePinned && e.isCurrentUser));

  const tabItems = [
    { key: 'friends', label: t('friends.tab.friends', 'Friends') },
    { key: 'leaderboard', label: t('friends.tab.leaderboard', 'Leaderboard') },
    { key: 'partnership', label: t('friends.tab.partnership', 'Partnership') },
  ];

  return (
    <div className="friends-container">
      <TabSwitch tabs={tabItems} activeTab={tab} onTabChange={setTab} />

      {/* ── FRIENDS TAB ─── */}
      {tab === 'friends' && (
        <div className="friends-tab-content">
          <p className="invite-count">{t('friends.total', 'Total: {{n}}', { n: friends.length })}</p>
          <p className="invite-subtitle">{t('friends.subtitle', 'Invite friends & get tickets')}</p>

          <div className="friends-list-scroll">
            <div className="friends-list">
              {isLoading && <Preloader />}
              {error && (
                <p style={{ textAlign: 'center', padding: '20px', color: '#ff6b6b' }}>{error}</p>
              )}
              {!isLoading && !error && friends.length === 0 && (
                <p style={{ textAlign: 'center', padding: '20px', opacity: 0.6 }}>
                  {t('friends.empty', 'No friends invited yet. Share your link to invite!')}
                </p>
              )}
              {friends.map((f) => {
                const name = [f.firstName, f.lastName].filter(Boolean).join(' ') || f.username || t('common.unknown', 'Unknown');
                return (
                  <div className="friend-card" key={f.id}>
                    <UserAvatar photoUrl={f.photoUrl} name={name} size={40} className="avatar" />
                    <span className="friend-name">{name}</span>
                    <div className="friend-action">
                      {f.ticketClaimed ? (
                        <img src="/stroke.png" alt="✓" className="friend-check" />
                      ) : (
                        <>
                          <img src="/ticket_icon.png" alt="ticket" className="ticket-icon-friends" />
                          <span className="coin-count">+1</span>
                          <button
                            className="app-btn app-btn--green app-btn--sm"
                            disabled={claimingId === f.id}
                            onClick={() => handleClaimTicket(f)}
                          >
                            {claimingId === f.id ? '...' : t('common.claim', 'Claim')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="friends-convert-div">
            <button className="app-btn app-btn--purple" onClick={() => setShowInvite(true)}>{t('friends.invite_button', 'Invite a friend')}</button>
          </div>
        </div>
      )}

      {/* ── LEADERBOARD TAB ─── */}
      {tab === 'leaderboard' && (
        <div className="leaderboard-tab-scroll">
          <div className="reward-container">
            <div className="reward-box">
              <div className="title-container">
                <p className="reward-title">{t('leaderboard.rewards', 'Rewards:')}</p>
                <p className="time-left">⏳ 58d 48h</p>
              </div>
              <div className="separator"></div>
              <div className="inline-reward-container">
                <div className="inline-reward-item">
                  <img src="/gram.png" alt="ton" />
                  <p><span className="top-reward">1000 GRAM</span> – {t('leaderboard.reward_top3', 'Shared by the top 3')}</p>
                </div>
                <div className="inline-reward-item">
                  <img src="/coin_icon.png" alt="coin" />
                  <p><span className="mid-reward">{t('leaderboard.reward_mid_amount', '10,000 coins')}</span> – {t('leaderboard.reward_mid', 'For 4th to 1,000th')}</p>
                </div>
                <div className="inline-reward-item">
                  <img src="/coin_icon.png" alt="coin" />
                  <p><span className="low-reward">{t('leaderboard.reward_low_amount', '5,000 coins')}</span> – {t('leaderboard.reward_low', 'For 1,001st to 10,000th')}</p>
                </div>
              </div>
            </div>
          </div>

          {lbLoading && <Preloader />}

          {!lbLoading && leaderboard.length > 0 && (
            <>
              <div className="podium">
                <div className="podium-background"></div>
                <div className="leader-background"></div>
                {podium.map(({ rank, displayName, photoUrl, coins, medal, spins }) => (
                  <div key={rank} className={`podium-user ${medal}`}>
                    <div className={"user-with-medal"}>
                      <UserAvatar photoUrl={photoUrl} name={displayName} size={56} className="medal-avatar" />
                      <img className="medal-icon" src={`/${medal}.png`} alt={medal} />
                    </div>
                    <p className="name">{displayName}</p>
                    <div className="podium-item">
                      <img className="item-icon spins" src="/loading_spinner_icon.png" alt="spinner" />
                      <p className="score">{spins.toLocaleString()}</p>
                    </div>
                    <div className="podium-item">
                      <img className="item-icon" src="/gram.png" alt="ton" />
                      <p className="tickets">{TON_BY_MEDAL[medal]}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="leaderboard-list">
                {listEntries.map(entry => (
                  <div
                    key={entry.rank}
                    className={`leader-row ${entry.isCurrentUser ? 'highlight' : ''}`}
                  >
                    <div className="avatar-placeholder">
                      <UserAvatar photoUrl={entry.photoUrl} name={entry.displayName} size={40} className="avatar" />
                      <div className="name-placeholder">
                        <span className="name">{entry.displayName}</span>
                        <span className="place">{t('leaderboard.place', 'Place: {{rank}}', { rank: entry.rank.toLocaleString() })}</span>
                      </div>
                    </div>
                    <div className="amounts-placeholder">
                      <div className="row-right">
                        <img src="/spinner.png" alt="coins" className="coin-icon" />
                        <span>{entry.spins.toLocaleString()}</span>
                      </div>
                      {!entry.isCurrentUser && (
                        <div className="row-right">
                          <img src="/coin_icon.png" alt="reward" className="coin-icon" />
                          <span>10,000</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {showMePinned && (
                <div className="leader-row leader-row--me-pinned highlight">
                  <div className="avatar-placeholder">
                    <UserAvatar photoUrl={me.photoUrl} name={me.displayName} size={40} className="avatar" />
                    <div className="name-placeholder">
                      <span className="name">{me.displayName}</span>
                      <span className="place">{t('leaderboard.place', 'Place: {{rank}}', { rank: me.rank.toLocaleString() })}</span>
                    </div>
                  </div>
                  <div className="amounts-placeholder">
                    <div className="row-right">
                      <img src="/spinner.png" alt="coins" className="coin-icon" />
                      <span>{me.spins.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── PARTNERSHIP TAB ─── */}
      {tab === 'partnership' && <PartnershipTab />}

      {showInvite && <InviteFriendsModal onClose={() => setShowInvite(false)} />}
    </div>
  );
};

export default FriendsLeaderboardPage;
